import { useState, useEffect } from "react";
import { useOutletContext } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { API } from "../../App";
import { 
  Eye, MessageCircle, Gift, BarChart2, 
  TrendingUp, Clock, Users 
} from "lucide-react";

export default function CoupleAnalytics() {
  const { wedding } = useOutletContext();
  const [analytics, setAnalytics] = useState(null);
  const [redirectLogs, setRedirectLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      if (!wedding?.wedding_id) return;
      try {
        const token = localStorage.getItem("token");
        const headers = { Authorization: `Bearer ${token}` };

        const [analyticsRes, logsRes] = await Promise.all([
          axios.get(`${API}/analytics/wedding/${wedding.wedding_id}`, { headers }),
          axios.get(`${API}/analytics/redirect-logs?wedding_id=${wedding.wedding_id}`, { headers }),
        ]);

        setAnalytics(analyticsRes.data);
        setRedirectLogs(logsRes.data.slice(0, 20)); // Last 20 logs
      } catch (error) {
        console.error("Failed to fetch analytics:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [wedding]);

  if (!wedding) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-wedding-ivory/60">No wedding assigned to your account.</p>
      </div>
    );
  }

  const stats = [
    { 
      label: "Page Views", 
      value: analytics?.page_views || 0, 
      icon: Eye,
      color: "text-blue-400"
    },
    { 
      label: "Tree Messages", 
      value: analytics?.tree_submissions || 0, 
      icon: MessageCircle,
      color: "text-green-400"
    },
    { 
      label: "Artifact Scans", 
      value: analytics?.artifact_scans || 0, 
      icon: Gift,
      color: "text-purple-400"
    },
    { 
      label: "Approved", 
      value: analytics?.approved_submissions || 0, 
      icon: TrendingUp,
      color: "text-wedding-gold"
    },
  ];

  return (
    <div className="space-y-8" data-testid="couple-analytics">
      {/* Header */}
      <div>
        <h1 className="font-serif text-3xl text-wedding-ivory">Analytics</h1>
        <p className="text-wedding-ivory/60 mt-1">Track your wedding page performance</p>
      </div>

      {/* Stats */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="w-12 h-12 border-2 border-wedding-gold border-t-transparent rounded-full animate-spin"></div>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {stats.map((stat, index) => (
              <motion.div
                key={stat.label}
                className="stat-card"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <stat.icon className={`w-6 h-6 ${stat.color} mb-3`} />
                <div className="stat-value">{stat.value}</div>
                <div className="stat-label">{stat.label}</div>
              </motion.div>
            ))}
          </div>

          {/* Top Artifacts */}
          {analytics?.top_artifacts?.length > 0 && (
            <div className="card-hover p-6">
              <h2 className="font-serif text-xl text-wedding-ivory mb-6 flex items-center gap-2">
                <BarChart2 className="w-5 h-5 text-wedding-gold" />
                Top Scanned Artifacts
              </h2>
              <div className="space-y-4">
                {analytics.top_artifacts.map((item, index) => (
                  <div 
                    key={item._id || index}
                    className="flex items-center justify-between py-3 border-b border-white/5 last:border-0"
                  >
                    <div className="flex items-center gap-3">
                      <span className="w-8 h-8 rounded-full bg-wedding-gold/10 flex items-center justify-center text-wedding-gold font-bold text-sm">
                        {index + 1}
                      </span>
                      <span className="text-wedding-ivory font-mono text-sm">
                        {item._id || "Unknown"}
                      </span>
                    </div>
                    <span className="text-wedding-gold font-bold">
                      {item.count} scans
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Recent Activity */}
          <div className="card-hover p-6">
            <h2 className="font-serif text-xl text-wedding-ivory mb-6 flex items-center gap-2">
              <Clock className="w-5 h-5 text-wedding-gold" />
              Recent Artifact Scans
            </h2>
            
            {redirectLogs.length === 0 ? (
              <p className="text-wedding-ivory/50 text-center py-8">No scans recorded yet</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Token</th>
                      <th>Destination</th>
                      <th>Time</th>
                    </tr>
                  </thead>
                  <tbody>
                    {redirectLogs.map((log) => (
                      <tr key={log.log_id}>
                        <td>
                          <code className="text-wedding-gold text-sm">{log.token}</code>
                        </td>
                        <td className="text-wedding-ivory/70 text-sm truncate max-w-[200px]">
                          {log.destination_url}
                        </td>
                        <td className="text-wedding-ivory/50 text-sm">
                          {new Date(log.accessed_at).toLocaleString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Engagement Summary */}
          <div className="grid md:grid-cols-2 gap-6">
            <div className="card-hover p-6">
              <h3 className="font-serif text-lg text-wedding-ivory mb-4 flex items-center gap-2">
                <Users className="w-5 h-5 text-wedding-gold" />
                Engagement Summary
              </h3>
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-wedding-ivory/60">Total Visitors</span>
                  <span className="text-wedding-ivory font-bold">{analytics?.page_views || 0}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-wedding-ivory/60">Guest Messages</span>
                  <span className="text-wedding-ivory font-bold">{analytics?.tree_submissions || 0}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-wedding-ivory/60">Conversion Rate</span>
                  <span className="text-wedding-gold font-bold">
                    {analytics?.page_views > 0 
                      ? ((analytics?.tree_submissions / analytics?.page_views) * 100).toFixed(1) 
                      : 0}%
                  </span>
                </div>
              </div>
            </div>

            <div className="card-hover p-6">
              <h3 className="font-serif text-lg text-wedding-ivory mb-4 flex items-center gap-2">
                <Gift className="w-5 h-5 text-wedding-gold" />
                Artifact Stats
              </h3>
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-wedding-ivory/60">Total Scans</span>
                  <span className="text-wedding-ivory font-bold">{analytics?.artifact_scans || 0}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-wedding-ivory/60">Unique Artifacts</span>
                  <span className="text-wedding-ivory font-bold">{analytics?.top_artifacts?.length || 0}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-wedding-ivory/60">Avg Scans/Artifact</span>
                  <span className="text-wedding-gold font-bold">
                    {analytics?.top_artifacts?.length > 0
                      ? (analytics?.artifact_scans / analytics?.top_artifacts?.length).toFixed(1)
                      : 0}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
